import { AxiosResponse } from "axios"

import { Permission, UserAndGroup } from "@framework/types/user"
import { PaginationListMeta, PaginationParams } from "@framework/types/utils"

import HttpService from "./http.service"

export interface GetUserAndGroupsResponse {
  data: UserAndGroup[]
  pagination: {
    totalItems: number
    totalPages: number
    page: number
    pageSize: number
  }
}

export interface GetPermittedUserAndGroupsResponse {
  data: Permission[]
  meta: PaginationListMeta
}

export type EntityAccessEndpoints = "solution" | "company" | "content-type"

export type GetEntitiesRequestFilter = {
  excludeByRelation: EntityAccessEndpoints
  relationId: string
}

export type GetEntitiesPaginationRequest = {
  page: number
  pageSize?: number
  query?: string
}

export type GetEntitiesRequest = GetEntitiesPaginationRequest &
  (GetEntitiesRequestFilter | {})

class EntityPermissionsApi extends HttpService {
  getUserAndGroups = (
    params: GetEntitiesRequest
  ): Promise<AxiosResponse<GetUserAndGroupsResponse>> =>
    this.get("ts/entities", true, params)

  getEntityPermissions = (
    entityId: string,
    entityType: EntityAccessEndpoints,
    params: PaginationParams
  ): Promise<AxiosResponse<GetPermittedUserAndGroupsResponse>> => {
    return this.get(
      `ts/users-permissions/${entityType}/${entityId}`,
      true,
      params
    )
  }

  grantAccess = (params: {
    entityIds: string[]
    entityType: EntityAccessEndpoints
    userIds: string[]
    groupIds: string[]
  }): Promise<AxiosResponse<{ data: string }>> => {
    const entityPropName =
      params.entityType === "company"
        ? "companyIds"
        : params.entityType === "content-type"
        ? "contentTypeIds"
        : null

    if (entityPropName == null) throw new Error("Unexpected parameter name")

    return this.post(
      `ts/users-permissions`,
      {
        productIds: [],
        documentIds: [],
        categoryIds: [],
        companyIds: [],
        contentTypeIds: [],
        [entityPropName]: params.entityIds,
        userIds: params.userIds,
        groupIds: params.groupIds,
      },
      true
    )
  }

  withdrawAccess = (
    entityId: string,
    entityType: EntityAccessEndpoints,
    params:
      | {
          userIds: string[]
          groupIds: string[]
          makePublic: false
        }
      | { makePublic: true }
  ): Promise<AxiosResponse<{ data: string }>> =>
    this.delete(`ts/users-permissions/${entityType}/${entityId}`, params, true)
}

export default new EntityPermissionsApi()
